/**
 * BeastLife — GA4 events, phase 2 (header + homepage extras + PDP)
 * ---------------------------------------------------------------------------
 * File:    assets/bl-ga4-events-2.js
 * Install: layout/theme.liquid, before </body>, after bl-ga4-events.js:
 *
 *   <script src="{{ 'bl-ga4-events-2.js' | asset_url }}" defer="defer"></script>
 *
 * Independent of phase 1 (own transport, own listeners). Both files can load
 * in any order. Nothing here re-fires an event phase 1 already covers.
 *
 * Events (GA4 snake_case ← client sheet name):
 *   hamburger_menu_clicked          ← Hamburger Menu Clicked
 *   logo_clicked                    ← Logo Clicked
 *   whatsapp_button_clicked         ← WhatsApp Button Clicked
 *   account_icon_clicked            ← Account Icon Clicked           (login_status)
 *   product_authentication_clicked  ← Product Authentication Clicked
 *   cart_icon_clicked               ← Cart Icon Clicked              (cart_item_count)
 *   search_icon_clicked             ← Search Icon Clicked            (fires on search-box focus; there is no icon)
 *   ticker_clicked                  ← Ticker Clicked
 *   featured_trusted_icon_clicked   ← Featured & Trusted Icon Clicked (press logos + cert badges, one event per logo)
 *   pdp_main_image_clicked          ← PDP Main Image Clicked
 *   pdp_thumbnail_clicked           ← PDP Thumbnail Clicked
 *   flavour_variant_clicked         ← Flavour Variant Clicked        (sibling-product link, not a Shopify variant)
 *   weight_variant_viewed           ← Weight Variant Viewed
 *   weight_variant_clicked          ← Weight Variant Clicked         (price prefetched from /products/<handle>.js)
 *   app_download_clicked            ← App Download Button Clicked
 *   bundle_selected                 ← Bundle Selected                (best effort — EasyGift widget, verify live)
 *   pincode_checked                 ← Pincode Checked
 *   about_product_viewed            ← About The Product Viewed
 *   aplus_content_viewed            ← A+ Content Viewed
 *   pdp_faq_expanded                ← PDP FAQ Expanded
 *   beastgpt_interaction            ← BeastGPT Interaction           (open = reliable; query only if Verifast posts it)
 *   reviews_section_viewed          ← Reviews Section Viewed
 *   recommended_product_clicked     ← Recommended Product Clicked
 *
 * Product ID on PDP = real SKU (ShopifyAnalytics.meta.product.variants[].sku).
 * Recommended-product cards = Shopify product ID (same rule as phase 1).
 *
 * Debug: ?bl_debug=1 → console logs + GA4 DebugView.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__blGa4Bound2) return;
  window.__blGa4Bound2 = true;
  if (window.Shopify && window.Shopify.designMode) return;

  /* ======================================================================
   * CONFIG
   * ==================================================================== */
  var CONFIG = {
    measurementId: 'G-70EHFXV66V',
    // 'path' → /products/x (same-origin), external links stay absolute. GA4 caps values at 100 chars.
    urlMode: 'path',
    // Prefetch /products/<handle>.js for each weight swatch so weight_variant_clicked carries the real price.
    prefetchWeightPrices: true,
    // "viewed" events fire once per pageview when the block is ≥50% visible
    // (or covers ≥50% of the viewport, for blocks taller than the screen).
    viewThreshold: 0.5,
    mirrorToDataLayer: false,
    debug: false
  };

  var DEBUG = CONFIG.debug || /[?&]bl_debug=1/.test(location.search);

  /* ======================================================================
   * SELECTORS
   * ==================================================================== */
  var SEL = {
    header: '.shopify-section-group-header-group, header, sticky-header',
    footer: 'footer, .shopify-section-group-footer-group',

    hamburger: 'summary.header__icon--menu, .header__icon--menu',
    logo: 'a.header__heading-link',
    whatsapp: 'a[href*="wa.me"], a[href*="whatsapp.com"], a.qrcodeicons',
    account: 'a.header__icon--accounts, [onclick*="kpHandleLogin"], #kp-login-button-header-logo, #kp-login-button-header-logo-mobile, #icon-kwikpass, #loggedin-icons',
    authentication: 'a[href*="/pages/authentication"]',
    cart: 'a.header__icon--cart, #cart-icon-bubble',
    cartCount: '.cart-count-bubble span[aria-hidden="true"], .cart-count-bubble',
    searchInput: '.st-search-input, #st-search, #st-search-mobile, input[name="q"], input[type="search"]',

    ticker: '.bl-ticker .bl-ti',

    // Trust logos — one event per logo, source_location tells them apart
    pressLogo: '.wbl__ft-logo-link, .wbl__ft-cell',
    qualityCert: '.cert-item',
    pdpCert: '.certifications .singleitem',

    // PDP
    mediaGallery: 'media-gallery, .product__media-wrapper',
    mediaItem: '.product__media-item',
    mediaExclude: '.slider-button, .thumbnail-list, .product__media-toggle-noop',
    thumbnail: '.thumbnail-list__item',
    flavourSwatch: '.color-swatch .swatch-item a',
    weightSwatchWrap: '.weight-swatch',
    weightSwatch: '.weight-swatch .swatch-item a',
    weightLabel: '.ws-weight',
    appDownload: '#bl-download-btn, .bl-app-banner, #appbrew-button, .appbrew-download-banner-download-button, #appbrew-download-banner-desktop-cta, #appbrew-download-confirmation-prompt-confirm-button, a[href*="play.google.com"], a[href*="apps.apple.com"]',
    bundleHint: /easygift|eg-bundle|bundle/i,        // matched against class + id along the composed path
    pincodeInput: '#pincode',
    pincodeButton: '.pincode-container button',
    pincodeResult: '#result',
    aboutSection: '.foraboutus, [id*="aboutthe_product"]',
    aplusSection: '.aplus-images, [id*="aplus_images"]',
    accordionButton: 'button.plusaccordion',
    beastGptToggle: '#toggleChatbot',
    reviewsWidget: '#judgeme_product_reviews, .jdgm-review-widget',
    reviewsData: '.jdgm-rev-widg, .jdgm-prev-badge',
    relatedSection: '.related-products, [id$="__related-products"]',
    productCard: '.product-card-wrapper, .card-wrapper',
    productCardExclude: '.quick-add, product-form, form[action*="/cart/add"], button, [class*="wishlist"]',
    cardTitleLink: '.card__heading a, a[id^="CardLink-"]',
    cardListItem: 'li.grid__item, .grid__item, .swiper-slide, li'
  };

  /* ======================================================================
   * TRANSPORT
   * ==================================================================== */
  function ensureGtag() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () { window.dataLayer.push(arguments); };
      if (DEBUG) console.warn('[BL GA4-2] window.gtag missing — installed shim.');
    }
  }

  function send(name, params) {
    var clean = {};
    for (var k in params) {
      if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
      var v = params[k];
      if (v === undefined || v === null || v === '' || (typeof v === 'number' && isNaN(v))) continue;
      clean[k] = typeof v === 'string' ? v.slice(0, 100) : v;
    }
    var payload = {};
    for (var p in clean) payload[p] = clean[p];
    payload.send_to = CONFIG.measurementId;
    payload.transport_type = 'beacon';
    if (DEBUG) payload.debug_mode = true;

    ensureGtag();
    try { window.gtag('event', name, payload); } catch (e) { if (DEBUG) console.error('[BL GA4-2]', e); }
    if (CONFIG.mirrorToDataLayer) {
      var dl = { event: name };
      for (var m in clean) dl[m] = clean[m];
      window.dataLayer.push(dl);
    }
    if (DEBUG) console.log('[BL GA4-2] ' + name, clean);
  }

  /* ======================================================================
   * HELPERS
   * ==================================================================== */
  function txt(el, max) {
    if (!el) return '';
    var t = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
    return t.slice(0, max || 100);
  }
  function altOf(el) {
    if (!el) return '';
    var img = el.tagName === 'IMG' ? el : el.querySelector('img');
    return img ? (img.getAttribute('alt') || '').trim() : '';
  }
  function imgName(el) {
    // "191a2ad3019f--cert-01-e6e6c8.avif" → "cert-01" ; "HACCP_T.png" → "HACCP_T"
    var img = el && (el.tagName === 'IMG' ? el : el.querySelector('img'));
    var src = img && (img.getAttribute('src') || '');
    if (!src) return '';
    var file = src.split('?')[0].split('/').pop().replace(/\.[a-z0-9]+$/i, '');
    if (file.indexOf('--') > -1) file = file.split('--').pop().replace(/-[0-9a-f]{6}$/i, '');
    return file;
  }
  function label(el, max) {
    if (!el) return '';
    var n = max || 100;
    return txt(el, n) || (el.getAttribute('aria-label') || '').slice(0, n) ||
           (el.getAttribute('title') || '').slice(0, n) || altOf(el).slice(0, n) || imgName(el).slice(0, n);
  }
  function url(href) {
    if (!href || /^(javascript:|#|mailto:|tel:)/i.test(href)) return '';
    try {
      var u = new URL(href, location.href);
      return ((CONFIG.urlMode === 'path' && u.origin === location.origin) ? u.pathname + u.search : u.href).slice(0, 100);
    } catch (e) { return String(href).slice(0, 100); }
  }
  function money(s) {
    var n = parseFloat(String(s || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? undefined : n;
  }
  function indexIn(list, el) {
    var i = Array.prototype.indexOf.call(list, el);
    return i < 0 ? undefined : i + 1;
  }
  function pageType() {
    var st = window.__st || {};
    if (st.p) return st.p;                       // index | product | collection | page | article | cart | search
    var p = location.pathname;
    if (p === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(p)) return 'index';
    if (/\/products\//.test(p)) return 'product';
    if (/\/collections\//.test(p)) return 'collection';
    if (/\/cart/.test(p)) return 'cart';
    if (/\/search/.test(p)) return 'search';
    return 'page';
  }
  function platform() {
    var ua = navigator.userAgent || '';
    if (/android/i.test(ua)) return 'android';
    if (/iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
    return /mobile/i.test(ua) ? 'other_mobile' : 'desktop';
  }
  function cookie(name) {
    var m = document.cookie.match('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }
  function storage(name) {
    try { return localStorage.getItem(name) || ''; } catch (e) { return ''; }
  }

  // KwikPass (GoKwik) login — Shopify customer accounts aren't used on this store.
  // Signals, in order of confidence: header markup the KwikPass snippet toggles,
  // the phone-number key it writes, then Shopify's __st.cid as a last resort.
  function loginStatus() {
    var loggedIn = document.querySelector('#loggedin-icons, #loggedin-icons-mobile');
    var loggedOut = document.querySelector('#kp-login-button-header-logo, #kp-login-button-header-logo-mobile');
    if (loggedIn && loggedIn.offsetParent !== null) return 'logged_in';
    if (loggedOut && loggedOut.offsetParent !== null) return 'logged_out';
    if (storage('notify_phone_number') || cookie('notify_phone_number')) return 'logged_in';
    try {
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i) || '';
        if (/gokwik|kwikpass|kp_/i.test(k) && /token/i.test(k) && localStorage.getItem(k)) return 'logged_in';
      }
    } catch (e) {}
    if (window.__st && window.__st.cid) return 'logged_in';
    return 'logged_out';
  }

  // Cart count: header bubble (Dawn only renders it when > 0) → cached /cart.js → 0
  var cartCache = null;
  function refreshCart() {
    if (!window.fetch) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { cartCache = c && typeof c.item_count === 'number' ? c.item_count : cartCache; })
      .catch(function () {});
  }
  function cartCount() {
    var el = document.querySelector(SEL.cartCount);
    var n = el ? parseInt((el.textContent || '').replace(/[^0-9]/g, ''), 10) : NaN;
    if (!isNaN(n)) return n;
    return cartCache === null ? 0 : cartCache;
  }

  // Current product on PDP
  var product = (function () {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    var p = meta && meta.product;
    if (!p) return null;
    var variants = p.variants || [];
    var selectedId = meta.selectedVariantId;
    var v = null;
    for (var i = 0; i < variants.length; i++) if (String(variants[i].id) === String(selectedId)) v = variants[i];
    v = v || variants[0] || {};
    return {
      id: String(p.id || ''),
      handle: p.handle || '',
      sku: v.sku || '',
      variantId: String(v.id || ''),
      name: v.name || document.title,
      price: typeof v.price === 'number' ? v.price / 100 : undefined
    };
  })();
  var ITEM_ID = product ? (product.sku || product.id) : '';
  var IS_PDP = !!product;

  /* ======================================================================
   * WEIGHT SWATCH PRICE PREFETCH  (/products/<handle>.js is CDN-cached JSON)
   * ==================================================================== */
  var siblingCache = {};
  function prefetchSiblings() {
    if (!CONFIG.prefetchWeightPrices || !window.fetch) return;
    var links = document.querySelectorAll(SEL.weightSwatch);
    var run = function () {
      Array.prototype.forEach.call(links, function (a) {
        var handle = ((a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/) || [])[1];
        if (!handle || siblingCache[handle]) return;
        siblingCache[handle] = { pending: true };
        fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (p) {
            var v = (p.variants || [])[0] || {};
            siblingCache[handle] = { price: typeof v.price === 'number' ? v.price / 100 : undefined, sku: v.sku || String(p.id || '') };
          })
          .catch(function () { siblingCache[handle] = {}; });
      });
    };
    if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 3000 }); else setTimeout(run, 1500);
  }

  /* ======================================================================
   * CLICK EVENTS
   * ==================================================================== */
  function trackHeaderish(target) {
    var el;
    if ((el = target.closest(SEL.hamburger)))  { send('hamburger_menu_clicked', { page_type: pageType() }); return true; }
    if ((el = target.closest(SEL.logo)))       { send('logo_clicked', { page_type: pageType(), destination_url: url(el.getAttribute('href')) }); return true; }
    if ((el = target.closest(SEL.whatsapp)))   { send('whatsapp_button_clicked', { page_type: pageType(), destination_url: url(el.getAttribute('href')) }); return true; }
    if ((el = target.closest(SEL.cart)))       { send('cart_icon_clicked', { page_type: pageType(), cart_item_count: cartCount() }); return true; }
    if ((el = target.closest(SEL.account)))    { send('account_icon_clicked', { page_type: pageType(), login_status: loginStatus() }); return true; }
    if ((el = target.closest(SEL.authentication)) && !el.closest(SEL.footer)) {
      send('product_authentication_clicked', {
        page_type: pageType(),
        source_location: el.closest('.menu-drawer, .custom-mobile-drawer') ? 'mobile_menu' : (el.closest(SEL.header) ? 'header_nav' : 'page'),
        destination_url: url(el.getAttribute('href'))
      });
      return true;
    }
    return false;
  }

  function trackTicker(target) {
    var el = target.closest(SEL.ticker);
    if (!el) return false;
    send('ticker_clicked', { page_type: pageType(), ticker_text: txt(el, 80) });
    return true;
  }

  function trackTrustIcon(target) {
    var el, source;
    if ((el = target.closest(SEL.pressLogo)))        source = 'featured_trusted';
    else if ((el = target.closest(SEL.qualityCert))) source = 'quality_certs';
    else if ((el = target.closest(SEL.pdpCert)))     source = 'pdp_certifications';
    if (!el) return false;
    var a = el.tagName === 'A' ? el : el.querySelector('a');
    send('featured_trusted_icon_clicked', {
      page_type: pageType(),
      icon_name: altOf(el) || imgName(el) || txt(el, 60) || 'unnamed',
      source_location: source,
      destination_url: a ? url(a.getAttribute('href')) : ''
    });
    return true;
  }

  function trackAppDownload(target) {
    var el = target.closest(SEL.appDownload);
    if (!el) return false;
    var source = el.closest('.bl-app-banner, #bl-download-btn') ? 'pdp_banner'
      : el.id === 'appbrew-download-banner-desktop-cta' || el.closest('#appbrew-download-banner-desktop-cta') ? 'appbrew_desktop_cta'
      : el.id === 'appbrew-download-confirmation-prompt-confirm-button' ? 'appbrew_prompt'
      : el.closest('#appbrew-download-banner-root') ? 'appbrew_bar'
      : 'store_link';
    send('app_download_clicked', {
      page_type: pageType(),
      platform: platform(),
      source_location: source,
      destination_url: url(el.getAttribute('href') || el.getAttribute('data-href') || '')
    });
    return true;
  }

  function trackPdpClick(target, e) {
    if (!IS_PDP) return false;
    var el;

    // Thumbnails before main media (thumbnail list can sit inside the gallery element)
    if ((el = target.closest(SEL.thumbnail))) {
      var pos = parseInt(el.getAttribute('data-media-position'), 10);
      send('pdp_thumbnail_clicked', { item_id: ITEM_ID, thumbnail_index: isNaN(pos) ? indexIn(el.parentElement.children, el) : pos });
      return true;
    }
    if ((el = target.closest(SEL.mediaItem)) && el.closest(SEL.mediaGallery) && !target.closest(SEL.mediaExclude)) {
      var items = el.parentElement ? el.parentElement.querySelectorAll(SEL.mediaItem) : [];
      send('pdp_main_image_clicked', { item_id: ITEM_ID, image_index: indexIn(items, el) });
      return true;
    }
    if ((el = target.closest(SEL.flavourSwatch))) {
      send('flavour_variant_clicked', { item_id: ITEM_ID, flavour_name: txt(el, 40) || altOf(el), destination_url: url(el.getAttribute('href')) });
      return true;
    }
    if ((el = target.closest(SEL.weightSwatch))) {
      var handle = ((el.getAttribute('href') || '').match(/\/products\/([^/?#]+)/) || [])[1];
      var sib = siblingCache[handle] || {};
      send('weight_variant_clicked', {
        item_id: ITEM_ID,
        weight_selected: txt(el.querySelector(SEL.weightLabel), 20) || txt(el, 20),
        price: sib.price,
        destination_url: url(el.getAttribute('href'))
      });
      return true;
    }
    if ((el = target.closest(SEL.accordionButton))) {
      var q = txt(el, 100);
      if (/^about the product/i.test(q)) markViewed('about_product_viewed', { item_id: ITEM_ID, interaction_type: 'expand' });
      else send('pdp_faq_expanded', { item_id: ITEM_ID, question: q });
      return true;
    }
    if ((el = target.closest(SEL.beastGptToggle))) {
      send('beastgpt_interaction', { item_id: ITEM_ID, interaction_type: 'open' });
      return true;
    }
    if ((el = target.closest(SEL.pincodeButton))) {
      pendingPincode = (document.querySelector(SEL.pincodeInput) || {}).value || '';
      pincodeArmed = true;
      return false; // result arrives async → MutationObserver sends the event
    }
    var related = target.closest(SEL.relatedSection);
    if (related && (el = target.closest(SEL.productCard)) && !target.closest(SEL.productCardExclude)) {
      var link = el.querySelector(SEL.cardTitleLink) || el.querySelector('a[href*="/products/"]');
      var href = (target.closest('a[href]') || link || {}).getAttribute ? (target.closest('a[href]') || link).getAttribute('href') : '';
      var pid = link && link.id ? (link.id.match(/-(\d+)$/) || [])[1] : '';
      var li = el.closest(SEL.cardListItem);
      send('recommended_product_clicked', {
        source_item_id: ITEM_ID,
        item_id: el.getAttribute('data-sku') || el.getAttribute('data-product-id') || pid || ((href || '').match(/\/products\/([^/?#]+)/) || [])[1],
        item_name: txt(link, 100) || altOf(el),
        card_position: li && li.parentElement ? indexIn(li.parentElement.children, li) : undefined,
        destination_url: url(href)
      });
      return true;
    }
    // Bundle (EasyGift) — widget is client-rendered, possibly in open shadow DOM. Walk the composed path.
    var path = e && e.composedPath ? e.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      if (!n || n.nodeType !== 1) continue;
      var sig = (n.className && typeof n.className === 'string' ? n.className : '') + ' ' + (n.id || '');
      if (SEL.bundleHint.test(sig)) {
        var card = n;
        send('bundle_selected', {
          item_id: ITEM_ID,
          bundle_name: (txt(card.querySelector('h1,h2,h3,h4,strong,[class*="title"]'), 60) || txt(card, 60) || 'unnamed'),
          bundle_price: money((txt(card, 200).match(/₹\s?[0-9,]+(\.\d+)?/) || [''])[0])
        });
        return true;
      }
    }
    return false;
  }

  function onClick(e) {
    if (e.type === 'auxclick' && e.button !== 1) return;
    var target = e.target && e.target.nodeType === 3 ? e.target.parentElement : e.target;
    if (!target || !target.closest) return;
    if (target.closest(SEL.footer)) return;        // footer_click (phase 1) owns the footer

    if (trackHeaderish(target)) return;
    if (trackAppDownload(target)) return;
    if (trackTicker(target)) return;
    if (trackTrustIcon(target)) return;
    trackPdpClick(target, e);
  }

  // Search: the header has an input, not an icon. Focus is the "open search" moment.
  var lastSearchFocus = 0;
  function onFocus(e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches(SEL.searchInput)) return;
    if (Date.now() - lastSearchFocus < 1000) return;
    lastSearchFocus = Date.now();
    send('search_icon_clicked', { page_type: pageType() });
  }

  /* ======================================================================
   * PINCODE  — cod-checker.js writes the outcome into #result
   * ==================================================================== */
  var pendingPincode = '', pincodeArmed = false;
  function watchPincode() {
    var result = document.querySelector(SEL.pincodeResult);
    if (!result || !window.MutationObserver) return;
    new MutationObserver(function () {
      if (!pincodeArmed) return;
      var t = txt(result, 120);
      if (!t || /checking/i.test(t)) return;
      var status = /expected delivery/i.test(t) ? 'available'
        : /no available courier|not available|unserviceable/i.test(t) ? 'not_available'
        : /valid 6-digit/i.test(t) ? 'invalid'
        : /error/i.test(t) ? 'error' : 'unknown';
      pincodeArmed = false;
      var input = document.querySelector(SEL.pincodeInput);
      send('pincode_checked', { item_id: ITEM_ID, pincode: pendingPincode || (input && input.value) || '', serviceability: status });
    }).observe(result, { childList: true, subtree: true, characterData: true });
  }

  /* ======================================================================
   * VIEWED EVENTS  — once per pageview
   * ==================================================================== */
  var viewed = {};
  function markViewed(name, params) {
    if (viewed[name]) return;
    viewed[name] = true;
    send(name, params);
  }
  function watchViews() {
    if (!IS_PDP || !('IntersectionObserver' in window)) return;
    var targets = [
      { sel: SEL.weightSwatchWrap, name: 'weight_variant_viewed', params: function (el) {
        var opts = Array.prototype.map.call(el.querySelectorAll(SEL.weightLabel), function (w) { return txt(w, 12); }).filter(Boolean);
        return { item_id: ITEM_ID, weight_options: opts.join(', ') };
      } },
      { sel: SEL.aboutSection, name: 'about_product_viewed', params: function () { return { item_id: ITEM_ID, interaction_type: 'scroll' }; } },
      { sel: SEL.aplusSection, name: 'aplus_content_viewed', params: function () { return { item_id: ITEM_ID }; } },
      { sel: SEL.reviewsWidget, name: 'reviews_section_viewed', params: function (el) {
        var d = el.querySelector(SEL.reviewsData) || document.querySelector(SEL.reviewsData);
        return {
          item_id: ITEM_ID,
          average_rating: d ? parseFloat(d.getAttribute('data-average-rating')) : undefined,
          review_count: d ? parseInt(d.getAttribute('data-number-of-reviews'), 10) : undefined
        };
      } }
    ];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var tallEnough = entry.intersectionRect.height >= window.innerHeight * CONFIG.viewThreshold;
        if (entry.intersectionRatio < CONFIG.viewThreshold && !tallEnough) return;
        var spec = entry.target.__blView;
        if (!spec) return;
        io.unobserve(entry.target);
        markViewed(spec.name, spec.params(entry.target));
      });
    }, { threshold: [0, 0.25, 0.5, 0.75] });
    targets.forEach(function (spec) {
      var el = document.querySelector(spec.sel);
      if (!el) return;
      el.__blView = spec;
      io.observe(el);
    });
  }

  /* ======================================================================
   * BEASTGPT  — Verifast chatbot lives in a third-party iframe. The theme
   * button posts "verifast-toggle-bot"; anything the widget posts back is
   * surfaced here. Query text is only available if Verifast includes it.
   * ==================================================================== */
  function watchBeastGpt() {
    if (!IS_PDP) return;
    window.addEventListener('message', function (e) {
      var d = e.data;
      if (!d || typeof d !== 'object') return;
      var name = d.event_name || d.eventName || d.type || d.event || '';
      var fromVerifast = /verifast/i.test(name) || /verifast/i.test(e.origin || '');
      if (!fromVerifast) return;
      if (DEBUG) console.log('[BL GA4-2] verifast message', e.origin, d);
      if (/toggle-bot/i.test(name) && d.event_source === 'button') return; // our own open — already sent
      var q = d.query || d.question || d.message || d.text || d.user_message || '';
      send('beastgpt_interaction', {
        item_id: ITEM_ID,
        interaction_type: String(name).replace(/^verifast[-_]?/i, '').slice(0, 40) || 'message',
        query: typeof q === 'string' ? q.slice(0, 100) : ''
      });
    });
  }

  /* ======================================================================
   * BOOT
   * ==================================================================== */
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);
  document.addEventListener('focusin', onFocus, true);

  function boot() {
    refreshCart();
    if (IS_PDP) { prefetchSiblings(); watchPincode(); watchViews(); watchBeastGpt(); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.BL_GA4_2 = { version: '1.0.0', config: CONFIG, selectors: SEL, send: send, product: product, loginStatus: loginStatus, cartCount: cartCount };
  if (DEBUG) console.log('[BL GA4-2] ready', { page: pageType(), item_id: ITEM_ID, pdp: IS_PDP });
})();